/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import * as Sentry from "@sentry/react";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import { sentryConfig } from "./sentry";
import { authenticationState } from "src/state/state";
import { ApiErrorCode } from "@9amhealth/openapi";

const ignoreErrorCodes = [401, 403, 404, 409, 422];

const ignoreNineAmHealthErrorCodes = new Set<number>([
  ApiErrorCode.AVATAR_NOT_FOUND,
  ApiErrorCode.INVALID_LOGIN_CREDENTIALS,
  ApiErrorCode.EMAIL_VERIFICATION_FAILED,
  ApiErrorCode.ADDRESS_NOT_FOUND,
  ApiErrorCode.PCP_CANNOT_BE_FOUND,
  // reported at the call site instead, where the request context is known
  ApiErrorCode.DATA_VALIDATION_FAILED,
  ApiErrorCode.EMAIL_VERIFICATION_REQUIRED,
  ApiErrorCode.PASSWORD_REUSE_NOT_ALLOWED
]);

/** Runs a side-effecting read that may throw on malformed error shapes; swallows any throw. */
const safeRun = (fn: () => void): void => {
  try {
    fn();
  } catch {
    // malformed shape — ignore and continue
  }
};

/**
 * Resolves HTTP status, 9am error code, and enriches `extra` from any input.
 * Runs for all inputs (Error and non-Error) so code-based filtering is
 * applied uniformly.
 *
 * Returns whether the error should be suppressed and a human-readable message
 * string (used when converting a non-Error object to an Error).
 */
function resolveErrorMetadata(
  e: unknown,
  extra: Record<string, unknown>
): { shouldIgnore: boolean; message: string } {
  let httpStatus = 0;
  let errorCode = "";
  let errorMessage = "";
  safeRun(() => {
    const trace = (e as any).traceId as string;
    extra.traceId = trace;
  });

  safeRun(() => {
    const body = (e as any).body.data as object;
    extra.resData = body;
  });

  safeRun(() => {
    errorMessage = (e as any).body.message;
  });

  safeRun(() => {
    if (!errorMessage) {
      errorMessage = (e as any).body.error;
    }
  });

  safeRun(() => {
    errorCode = (e as any).body.code;
  });

  safeRun(() => {
    if (!errorCode) {
      errorCode = (e as any).body.status;
    }
    httpStatus = parseInt(errorCode, 10);
  });

  // == res object in error ==
  safeRun(() => {
    const res = (e as any).res as Response;
    httpStatus = res.status;
    if (!errorCode) {
      errorCode = res.status.toString();
    }
    const uri = new URL(res.url);
    const path = uri.pathname;
    extra.path = path;
  });

  // == openapi ApiError shape: status/url/body live at the top level (no `.res`) ==
  // Guard on the ApiError name so a stray numeric `status` on an unrelated
  // object can't be mistaken for an HTTP status and trigger false filtering.
  safeRun(() => {
    if ((e as any).name !== "ApiError") {
      return;
    }
    const status = (e as any).status as number;
    if (typeof status !== "number" || Number.isNaN(status)) {
      return;
    }
    httpStatus = status;
    if (!errorCode) {
      errorCode = status.toString();
    }
    if (!extra.path) {
      extra.path = new URL((e as any).url as string).pathname;
    }
  });

  const message = `Error: ${errorMessage} | Code: ${errorCode}`;

  const shouldIgnore =
    ignoreErrorCodes.includes(httpStatus) ||
    ignoreNineAmHealthErrorCodes.has(Number(errorCode));

  return { shouldIgnore, message };
}

export default function reportErrorSentry(
  e: Error | unknown,
  addExtraData: Record<string, unknown> = {},
  options?: { expected?: boolean; level?: Sentry.SeverityLevel }
): void {
  if (!e) {
    return;
  }
  addSentryBreadcrumb("errorHandler", "Captured error", "error", {
    event: e
  });

  const extra: Record<string, unknown> = {
    ...addExtraData,
    hasAuthToken: Boolean(authenticationState.accessToken),
    hasRefreshToken: Boolean(authenticationState.refreshToken),
    authStatus: authenticationState.state.authenticationStatus
  };

  // Resolve code-based filtering and enrich extra for ALL inputs
  const { shouldIgnore, message: resolvedMessage } = resolveErrorMetadata(
    e,
    extra
  );

  // Handle expected errors — leave a breadcrumb but never capture
  if (options?.expected) {
    const msg = e instanceof Error ? e.message : resolvedMessage;
    addSentryBreadcrumb("errorHandler", msg, "info", extra);
    console.info("Expected error (not sent to Sentry):", e, extra);
    return;
  }

  if (shouldIgnore) {
    return;
  }

  // convert to Error if not already
  if (!(e instanceof Error)) {
    if (typeof e === "string") {
      e = new Error(e);
    } else {
      // mark non-Error objects for extra context
      extra.object = e;
      e = new Error(resolvedMessage || "Unknown error");
    }
  }

  const level: Sentry.SeverityLevel = options?.level ?? "error";

  if (sentryConfig.enabled) {
    Sentry.captureException(e, {
      level,
      extra
    });
  }

  if (!sentryConfig.enabled) {
    console.info(
      "Extra Info for following error was not sent to Sentry:",
      extra
    );

    console.error(e);
  }
}
